import { z } from "zod";
import { SHOP_CLASSES } from "../constants";
import type { ShopClass, ShopCreateInput, ShopLocation } from "../types";
import { cleanText, normalizePhone } from "../../../shared/utils/format";

const shopClassSchema = z.enum(SHOP_CLASSES as [ShopClass, ...ShopClass[]]);

export const shopLocationSchema = z.object({
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  addressText: z.string().nullable(),
  status: z.enum(["exact", "text_only", "missing"])
});

export const manualShopSchema = z
  .object({
    shopName: z.string().trim().min(1, "Shop name is required."),
    shopPhoneNumber: z.string().nullable(),
    shopClass: shopClassSchema.nullable(),
    shopGovenorate: z.string().nullable(),
    shopCity: z.string().nullable(),
    shopDistrict: z.string().nullable(),
    shopStreet: z.string().nullable(),
    shopLocation: shopLocationSchema
  })
  .refine(
    (value) =>
      value.shopLocation.status === "exact" &&
      typeof value.shopLocation.latitude === "number" &&
      typeof value.shopLocation.longitude === "number",
    {
      message: "Select an exact shop location on the map.",
      path: ["shopLocation"]
    }
  );

export type ManualShopFormValues = {
  shopName: string;
  shopPhoneNumber: string;
  shopClass: "" | ShopClass;
  shopGovenorate: string;
  shopCity: string;
  shopDistrict: string;
  shopStreet: string;
  shopLocation: ShopLocation;
};

export type ManualShopValidation = {
  data: ShopCreateInput | null;
  errors: Partial<Record<keyof ManualShopFormValues | "form", string>>;
};

export function emptyManualForm(): ManualShopFormValues {
  return {
    shopName: "",
    shopPhoneNumber: "",
    shopClass: "",
    shopGovenorate: "",
    shopCity: "",
    shopDistrict: "",
    shopStreet: "",
    shopLocation: {
      latitude: null,
      longitude: null,
      addressText: null,
      status: "missing"
    }
  };
}

export function toManualFormValues(shop: {
  shopName: string;
  shopPhoneNumber: string | null;
  shopClass: ShopClass | null;
  shopGovenorate: string | null;
  shopCity: string | null;
  shopDistrict: string | null;
  shopStreet: string | null;
  shopLocation: ShopLocation;
}): ManualShopFormValues {
  return {
    shopName: shop.shopName,
    shopPhoneNumber: shop.shopPhoneNumber ?? "",
    shopClass: shop.shopClass ?? "",
    shopGovenorate: shop.shopGovenorate ?? "",
    shopCity: shop.shopCity ?? "",
    shopDistrict: shop.shopDistrict ?? "",
    shopStreet: shop.shopStreet ?? "",
    shopLocation: shop.shopLocation
  };
}

export function validateManualShop(values: ManualShopFormValues): ManualShopValidation {
  const candidate = {
    shopName: values.shopName,
    shopPhoneNumber: normalizePhone(values.shopPhoneNumber),
    shopClass: values.shopClass || null,
    shopGovenorate: cleanText(values.shopGovenorate),
    shopCity: cleanText(values.shopCity),
    shopDistrict: cleanText(values.shopDistrict),
    shopStreet: cleanText(values.shopStreet),
    shopLocation: values.shopLocation
  };

  const result = manualShopSchema.safeParse(candidate);

  if (!result.success) {
    const errors: ManualShopValidation["errors"] = {};

    for (const issue of result.error.issues) {
      const key = issue.path[0] as keyof ManualShopFormValues | undefined;
      errors[key ?? "form"] = issue.message;
    }

    return {
      data: null,
      errors
    };
  }

  return {
    data: {
      ...result.data,
      source: "manual",
      importBatchId: null
    },
    errors: {}
  };
}
